import { FC, useEffect, useRef, useState } from 'react';
import { ClickAwayListener, InputAdornment, TextField, TextFieldProps } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from '@mui/icons-material/Cancel';
import { useDebounce } from 'react-use';

export type ISearchboxProps = TextFieldProps & {
	alwaysExpanded?: boolean;
	debounce?: number;
	searchText?: string;
	testId?: string;
	onSearchTextChange?: (searchText: string) => void;
};

export const Searchbox: FC<ISearchboxProps> = ({
	alwaysExpanded = false,
	debounce = 500,
	searchText = '',
	testId,
	onSearchTextChange,
	sx,
	...props
}) => {
	const inputRef = useRef<HTMLInputElement>();
	const [searchValue, setSearchValue] = useState('');
	const [expanded, setExpanded] = useState(alwaysExpanded);
	const isExpanded = alwaysExpanded ? true : expanded;

	const handleOnClick = () => (setExpanded(true), inputRef.current?.focus());
	const handleClickAway = () => !searchValue && setExpanded(false);

	useDebounce(() => onSearchTextChange?.(searchValue), debounce, [searchValue]);
	useEffect(() => setSearchValue(searchText), [searchText]);
	useEffect(() => setExpanded(searchValue ? true : expanded), [searchValue]);

	return (
		<ClickAwayListener onClickAway={handleClickAway}>
			<TextField
				{...props}
				size="small"
				value={searchValue}
				onChange={(e) => setSearchValue?.(e.target.value)}
				onClick={handleOnClick}
				sx={{
					'&.MuiTextField-root': {
						height: 'fit-content',
						cursor: isExpanded ? undefined : 'pointer'
					},
					...sx
				}}
				inputRef={inputRef}
				InputProps={{
					inputProps: {
						'data-testid': testId
					},
					startAdornment: (
						<InputAdornment
							position="start"
							sx={{ color: 'inherit', minWidth: 48, height: 48, margin: 0, justifyContent: 'center' }}
						>
							<SearchIcon data-testid={`${testId}-search-icon`} color="inherit" />
						</InputAdornment>
					),
					endAdornment: (
						<InputAdornment
							position="end"
							sx={{
								color: 'inherit',
								maxWidth: isExpanded ? undefined : 0,
								minWidth: isExpanded ? 48 : 0,
								height: 48,
								margin: 0,
								justifyContent: 'center'
							}}
						>
							<CancelIcon
								color="inherit"
								sx={{ cursor: 'pointer', color: searchValue ? undefined : 'transparent' }}
								onClick={() => setSearchValue('')}
							/>
						</InputAdornment>
					),
					sx: {
						'&.MuiOutlinedInput-root': {
							padding: 0,
							maxHeight: isExpanded ? undefined : 48,
							maxWidth: isExpanded ? undefined : 48,
							height: isExpanded ? undefined : 48,
							width: isExpanded ? undefined : 48,
							cursor: 'inherit',
							backgroundColor: isExpanded ? 'common.white' : 'transparent',
							borderRadius: 3
						},
						'.MuiOutlinedInput-input': {
							width: isExpanded ? undefined : 0,
							height: 32
						}
					}
				}}
			/>
		</ClickAwayListener>
	);
};
